<template>
  <div class="h-full">
    <audio autoplay muted ref="audioRef" v-show="cameraType.isMicrophoneOn"></audio>
    <div v-show="cameraType.isCameraOn" class="h-full w-full relative">
      <ViceLoader />
      <video
        autoplay
        muted
        ref="videoRef"
        :class="['h-full w-full mirrorVideo', showControls && 'rounded-sm']"
      ></video>
    </div>

    <div
      :class="[
        'flex justify-center items-center h-full w-full bg-vice-medium-teal',
        showControls && 'rounded-lg rounded-lg',
      ]"
      v-show="!cameraType.isCameraOn"
    >
      <VideoOff
        class="h-20 w-20"
        :name="cameraType.name"
        :profile_photo_url="cameraType.profilePhoto"
      />
    </div>

    <p
      class="
        flex
        justify-center
        items-center
        capitalize
        px-2
        text-sm
        font-medium
        text-white
        pointer-events-none
        absolute
        bottom-0
        left-0
        z-5
      "
      v-if="showTitle"
    >
      {{ cameraType.name }}
    </p>
    <section
      class="absolute justify-end grid h-full bottom-0 right-0 text-left py-1 content-between"
      v-show="showControls"
    >
      <!-- Temporary remove mute and toggle option -->
      <!-- <div class="inline-flex mr-5">
        <ViceMicrophone class="h-6 w-6 mr-2" :muted="!cameraType.isMicrophoneOn" />
        <Toggle :enabled="cameraType.isMicrophoneOn" @click="toggleUserMicrophone" />
      </div>
      <div class="inline-flex">
        <VideoCameraIcon class="h-6 w-6 mr-2" />
        <Toggle :enabled="cameraType.isCameraOn" @click="toggleUserCamera" />
      </div> -->

      <!-- TODO: adapt new designs to these buttons -->
      <button
        data-cy="remove-external-camera"
        class="block"
        title="Stop Stream"
        @click="removeCamera"
      >
        <TrashIcon class="h-6 w-6 shadow-filter" />
      </button>

      <button
        data-cy="open-external-camera-settings"
        v-if="!isExternalPreview"
        class="block"
        title="Switch Stream"
        @click="openCamerasAVSettingsModal"
      >
        <CogIcon class="h-6 w-6 shadow-filter" />
      </button>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { CogIcon, TrashIcon } from '@heroicons/vue/solid';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { DISPLAY_NAME, MEDIA_TYPE } from '@/Utils/vice-constants';

export default {
  emits: ['hasMediaPermissionError'],
  props: {
    showControls: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    cameraType: {
      type: Object,
      required: true,
    },

    toggleCamera: {
      required: false,
    },
    toggleMicrophone: {
      required: false,
    },
    setVideoRef: {
      type: Function,
      required: true,
    },

    setAudioRef: {
      type: Function,
      required: true,
    },
    isExternalPreview: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ViceLoader,
    VideoOff,
    CogIcon,
    TrashIcon,
  },

  setup(props) {
    const isVisiblePermissionAlert = ref(false);
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const videoRef = ref();
    const audioRef = ref();

    const toggleUserCamera = () => {
      props.toggleCamera(props.cameraType.name);
    };

    const openCamerasAVSettingsModal = () => {
      multiCameraDevicesStore.setCurrentActiveCameraType(props.cameraType.name);
      multiCameraDevicesStore.openAVSettingsModal();
    };

    const removeCamera = () => {
      if (props.isExternalPreview) {
        multiCameraDevicesStore.removeExternalDevicePreview(props.cameraType.name);
      } else multiCameraDevicesStore.removeCamera(props.cameraType.name);
      multiCameraDevicesStore.markSessionSetupAsChanged();
    };

    const toggleUserMicrophone = () => {
      props.toggleMicrophone(props.cameraType.name);
    };
    onMounted(async () => {
      props.setVideoRef(videoRef.value, props.cameraType.name);
      props.setAudioRef(audioRef.value, props.cameraType.name);

      if (!props.isExternalPreview) {
        await multiCameraDevicesStore.activateMedia();
      }
    });

    // To Preview SmartGlass and Medical Device After scanning the QR
    const externalVideoTrack = computed(() =>
      props.cameraType.name === DISPLAY_NAME.VUZIX
        ? props.cameraType?.tracks?.find((track) => track.getType() === MEDIA_TYPE.VIDEO)
        : props.cameraType?.localTracks?.find((track) => track.getType() === MEDIA_TYPE.VIDEO),
    );
    watch(videoRef, (newRef) => {
      if (newRef && externalVideoTrack.value) externalVideoTrack.value.attach(newRef);
    });
    watch(externalVideoTrack, (newTrack) => {
      if (newTrack && videoRef.value) newTrack.attach(videoRef.value);
    });

    return {
      removeCamera,
      toggleUserCamera,
      videoRef,
      audioRef,
      toggleUserMicrophone,
      isVisiblePermissionAlert,
      openCamerasAVSettingsModal,
    };
  },
};
</script>

<style scoped>
.mirrorVideo {
  transform: scale(-1, 1); /*For Firefox (& IE) */
  -webkit-transform: scale(-1, 1); /*for Chrome & Opera (& Safari) */
}
</style>
